import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useContextId } from '@src/data/hooks';
import { useModel } from '@src/generic/model-store';
import { VERIFIED_MODES } from '@src/constants';
import TranslationSelection from './translation-selection';
import { fetchTranslationConfig } from './data/api';

const UnitTranslationSelector = ({
  dispatch,
  id,
  unitId,
  unitTitle,
  isLegacyButton,
}) => {
  const courseId = useContextId();
  const {
    language,
    enrollmentMode,
    accessExpiration,
    datesBannerInfo,
  } = useModel('coursewareMeta', courseId);
  const courseLanguage = language || 'en';
  const isEnglish = courseLanguage === 'en';
  const {
    courseModes,
    isStaff,
    verifiedMode,
    org,
    originalUserIsStaff,
    title: courseName,
  } = useModel('courseHomeMeta', courseId);
  const [translationConfig, setTranslationConfig] = useState({
    enabled: false,
    availableLanguages: [],
  });
  const hasVerifiedTrack = courseModes?.some(mode => VERIFIED_MODES.includes(mode.slug));

  const isFBE = !!accessExpiration && !!datesBannerInfo?.contentTypeGatingEnabled;
  const logEventProperties = {
    org_key: org,
    courserun_key: courseId,
    course_name: courseName,
  };

  useEffect(() => {
    if (hasVerifiedTrack) {
      fetchTranslationConfig(courseId).then(setTranslationConfig);
    }
  }, []);

  const { enabled, availableLanguages } = translationConfig;

  if (!hasVerifiedTrack || !enabled || !availableLanguages.length || !isEnglish) {
    return null;
  }

  let isAuditMode = enrollmentMode === 'audit' && !isStaff;

  /* 12-20-2024
   * This check will render different experiences between the learner audit view and the masquerade
   * audit view. The learner view will not see the button, but the masquerade view will show the
   * verified version of the button. Currently there is no way to distinguish the enrollmentMode
   * when masquerading, as it will always return audit. When masquerading and verifiedMode === null,
   * it is impossible to distinguish between the audit and verified experience from the courseware data.
  */
  if (isAuditMode) {
    if (!verifiedMode) {
      if (!originalUserIsStaff) {
        return null;
      }
      isAuditMode = false;
    }
  }

  return (
    <TranslationSelection
      {...{
        dispatch,
        id,
        courseId,
        courseLanguage,
        availableLanguages,
        unitId,
        unitTitle,
        isAuditMode,
        isFBE,
        logEventProperties,
        isLegacyButton,
      }}
      verifiedMode={verifiedMode || {}}
    />
  );
};

UnitTranslationSelector.propTypes = {
  id: PropTypes.string.isRequired,
  unitId: PropTypes.string.isRequired,
  unitTitle: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  isLegacyButton: PropTypes.bool.isRequired,
};

export default UnitTranslationSelector;
